import React, { useState } from "react";
import PropTypes from "prop-types";
import { Title, Fieldset, Button, Control, Field, Column, Label } from "rbx";
import { useMutation, useApolloClient } from "react-apollo";
import { SystemCodeSelect, BooleanInput } from "../inputs";
import {
  UPDATE_INSPECTION_MASTER_MUTATION,
  ALL_INSPECTION_MASTER_QUERY,
  DELETE_INSPECTION_MASTER_MUTATION
} from "../../graphql/InspectionMaster/InspectionMaster.graphql";
import {
  ALL_INSPECTIONS_QUERY,
  DELETE_INSPECTION_MUTATION
} from "../../graphql/Inspections/Inspections.graphql";

import { ourToast as toast } from "../../utils";

class CustomInspectionMasterError extends Error {}

const EditInspectionMasterForm = ({ onComplete, inspectionMaster }) => {
  const [inputs, setInputs] = useState({ ...inspectionMaster });
  const client = useApolloClient();
  const [loading, setLoading] = useState(false);
  const [editInspectionMaster, res] = useMutation(
    UPDATE_INSPECTION_MASTER_MUTATION,
    {
      refetchQueries: ["ALL_INSPECTION_CODES"]
    }
  );

  const onChange = e => {
    const { name } = e.target;
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setInputs({ ...inputs, [name]: value });
  };

  const handleSave = async e => {
    e.preventDefault();
    try {
      if (inputs && inputs.InspecCdRecIdRef && inputs.VehicleTypeRecIdRef) {
        const {
          data: { InspectionMasters }
        } = await client.query({
          query: ALL_INSPECTION_MASTER_QUERY,
          variables: {
            where: {
              InspecCdRecIdRef: parseInt(inputs.InspecCdRecIdRef),
              VehicleTypeRecIdRef: parseInt(inputs.VehicleTypeRecIdRef)
            },
            first: 1
          },
          fetchPolicy: "network-only"
        });
        if (
          InspectionMasters &&
          InspectionMasters.length > 0 &&
          parseInt(inputs.InspecCdRecIdRef) !==
            parseInt(inspectionMaster.InspecCdRecIdRef) &&
          parseInt(inputs.VehicleTypeRecIdRef) !==
            parseInt(inspectionMaster.VehicleTypeRecIdRef)
        ) {
          toast.error("This Inspection Code already exists.");
        } else {
          await editInspectionMaster({
            variables: {
              data: {
                Active: inputs.Active || inputs.Active === "on",
                InspecCdRecIdRef: parseInt(inputs.InspecCdRecIdRef),
                VehicleTypeRecIdRef: parseInt(inputs.VehicleTypeRecIdRef),
                OdometerReq: inputs.OdometerReq
              },
              where: {
                RecId: parseInt(inputs.RecId)
              }
            }
          });
          toast.success("Inspection Code updated successfully.");
          onComplete();
        }
      } else {
        toast.error("You have to select Inspection Code and Vehicle Type.");
      }
    } catch (err) {
      toast.error("Error creating Inspection Code.");
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      const { data: InspectionsData } = await client.query({
        query: ALL_INSPECTIONS_QUERY,
        variables: {
          where: {
            InspectionMasterRecIdRef: parseInt(inspectionMaster.RecId, 10)
          }
        }
      });
      await Promise.all(
        InspectionsData.Inspections.map(inspection =>
          client.mutate({
            mutation: DELETE_INSPECTION_MUTATION,
            variables: { where: { RecId: parseInt(inspection.RecId, 10) } }
          })
        )
      );
      await client.mutate({
        mutation: DELETE_INSPECTION_MASTER_MUTATION,
        variables: {
          where: {
            RecId: parseInt(inspectionMaster.RecId, 10)
          }
        },
        refetchQueries: ["ALL_INSPECTION_MASTER_QUERY"]
      });
      toast.success("Inspection Master deleted successfully.");
      onComplete();
    } catch (err) {
      const message =
        err instanceof CustomInspectionMasterError
          ? err.message
          : "Whoops! Something went wrong deleting this inspection master. Please contact support.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      className="add-item-form inspection-modal-container"
      id="add-item-form"
      onSubmit={handleSave}
    >
      <Title>Edit Inspection</Title>
      <Fieldset disabled={res.loading}>
        <Column.Group>
          <Column>
            <Control expanded>
              <SystemCodeSelect
                icon="globe"
                label="Inspection Code"
                lookup={{ Category: "Inventory", Code: "InspectionCd" }}
                name="InspecCdRecIdRef"
                size=""
                value={inputs?.InspecCdRecIdRef}
                onChange={onChange}
              />
            </Control>
          </Column>
          <Column>
            <Control expanded>
              <SystemCodeSelect
                icon="truck"
                label="Vehicle Type"
                lookup={{ Category: "Fleet", Code: "VehicleType" }}
                name="VehicleTypeRecIdRef"
                size=""
                value={inputs?.VehicleTypeRecIdRef}
                onChange={onChange}
              />
            </Control>
          </Column>
        </Column.Group>
        <Column.Group>
          <Column>
            <Control expanded>
              <Label>Odometer Reading Required</Label>
              <BooleanInput
                name="OdometerReq"
                value={inputs.OdometerReq}
                onChange={onChange}
              />
            </Control>
          </Column>
          <Column>
            <Control expanded>
              <Label>Active</Label>
              <BooleanInput
                name="Active"
                value={inputs.Active}
                onChange={onChange}
              />
            </Control>
          </Column>
        </Column.Group>
        <hr />
        <Field kind="group">
          <Control>
            <Button color="primary" form="add-item-form" type="submit">
              Submit
            </Button>
          </Control>
          <Control expanded>
            <Button type="button" onClick={onComplete}>
              Cancel
            </Button>
          </Control>
          <Control>
            <Button
              color="danger"
              disabled={loading}
              state={loading ? "loading" : ""}
              type="button"
              onClick={handleDelete}
            >
              Delete
            </Button>
          </Control>
        </Field>
      </Fieldset>
    </form>
  );
};

EditInspectionMasterForm.propTypes = {
  onComplete: PropTypes.func,
  inspectionMaster: PropTypes.object
};
export default EditInspectionMasterForm;
